import React, { useState, useContext } from 'react'
import ReactDOM from 'react-dom'
import { useMutation, useQuery } from '@apollo/client'
import mammoth from 'mammoth/mammoth.browser'
import DavidImage from '../images/David.jpg'
import SelinayImage from '../images/Selinay.jpg'
import TomasImage from '../images/Tomas.jpg'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import { GET_PROJECTS_QUERY, ADD_TASK_GROUP_WITH_TASKS_MUTATION } from '../query/gqlQueries.js'
import { streamOllamaGenerate } from '../ollama.js'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'

const ASSIGNEES = [
  { name: 'David', image: DavidImage },
  { name: 'Selinay', image: SelinayImage },
  { name: 'Tomas', image: TomasImage },
]
// Photography/Video first: the type doubles as the expected output medium,
// which drives which media folder the finished task links to (Phase 2).
const TYPES = ['Photography', 'Video', 'Design', '3D', 'Admin', 'Coding', 'Learning']
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent']
const HOUR_PRESETS = [0.5, 1, 2, 4, 8]
const MODELS = ['gemma4:26b', 'gemma3:27b']

// Pull plain text out of a supported document. docx via mammoth; txt/md read
// directly. (pdf support can be added with pdfjs later.)
async function extractText(file) {
  const name = file.name.toLowerCase()
  if (name.endsWith('.docx')) {
    const arrayBuffer = await file.arrayBuffer()
    const { value } = await mammoth.extractRawText({ arrayBuffer })
    return value
  }
  if (name.endsWith('.txt') || name.endsWith('.md') || file.type.startsWith('text/')) {
    return await file.text()
  }
  throw new Error(`Unsupported file type: ${file.name}. Use .docx or .txt for now.`)
}

export default function AddTaskGroupDialog() {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)

  const [file, setFile] = useState(null)
  const [status, setStatus] = useState('idle') // idle | analyzing | reviewing | saving | completed | error
  const [errorMsg, setErrorMsg] = useState('')
  const [progressMsg, setProgressMsg] = useState('')
  const [model, setModel] = useState(MODELS[0])
  const [projectName, setProjectName] = useState('')
  const [groupTitle, setGroupTitle] = useState('')
  const [tasks, setTasks] = useState([]) // [{title, section, type, description, priority, assignee, timeBudget}]
  const [bulkHours, setBulkHours] = useState('')

  const { data: projectsData } = useQuery(GET_PROJECTS_QUERY, { variables: { username }, skip: !username })
  const existingProjects = projectsData?.getProjects || []
  const [addTaskGroupWithTasks] = useMutation(ADD_TASK_GROUP_WITH_TASKS_MUTATION)

  const isOpen = dialogStates['dialogAddTaskGroup']

  const handleClose = () => {
    closeDialog('dialogAddTaskGroup')
    setFile(null); setStatus('idle'); setErrorMsg(''); setProgressMsg('')
    setProjectName(''); setGroupTitle(''); setTasks([]); setBulkHours('')
  }

  const handleFile = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    if (!groupTitle) setGroupTitle(f.name.replace(/\.[^.]+$/, ''))
  }

  const handleAnalyze = async () => {
    if (!file) return
    setStatus('analyzing')
    setErrorMsg('')
    setProgressMsg('Reading document…')
    try {
      const text = await extractText(file)
      if (!text.trim()) throw new Error('No readable text found in the document.')

      setProgressMsg('Asking the Mac Studio to break it into tasks…')
      const prompt = `You are a production manager turning a content brief into a task list.

From the document below, create ONE task per concrete deliverable (each individual video, carousel, photo set, testimonial, image, etc.). Do not merge deliverables and do not invent extras.

For each task provide:
- "title": a short actionable title, e.g. "Video: Tour of Cork Matters"
- "section": the campaign or section it belongs to, exactly as named in the document (or "General")
- "type": one of ${TYPES.join(', ')}. Use "Video" for any video/reel/testimonial-video deliverable, and "Photography" for photos, carousels, static images, and photo testimonials.
- "description": the key requirements (format, length, aspect ratio, notes) in 1-2 sentences
- "priority": one of ${PRIORITIES.join(', ')}
- "estimatedHours": a realistic number of hours for one person to complete this single deliverable, as a number (e.g. 0.5, 1, 2, 4). Base it on the effort implied by the description.

Return ONLY valid JSON in exactly this shape, nothing else:
{"tasks":[{"title":"...","section":"...","type":"Design","description":"...","priority":"Medium","estimatedHours":2}]}

Document:
${text}`

      const raw = await streamOllamaGenerate({
        body: { model, prompt, format: 'json', options: { temperature: 0.2, num_predict: 2000 } },
        onProgress: (acc) => setProgressMsg(`Structuring tasks… (${acc.length} chars)`),
      })
      const cleaned = (raw || '').replace(/```json/g, '').replace(/```/g, '').trim()
      const parsed = JSON.parse(cleaned)
      const list = (parsed.tasks || [])
        .filter((t) => t && t.title)
        .map((t) => ({
          title: String(t.title).trim(),
          section: (t.section || 'General').trim(),
          type: TYPES.includes(t.type) ? t.type : 'Photography',
          description: (t.description || '').trim(),
          priority: PRIORITIES.includes(t.priority) ? t.priority : 'Medium',
          assignee: '',
          // Pre-fill the AI's hours estimate; blank if it gave nothing sane.
          timeBudget: Number.isFinite(Number(t.estimatedHours)) && Number(t.estimatedHours) > 0
            ? String(Number(t.estimatedHours))
            : '',
        }))
      if (list.length === 0) throw new Error('No tasks could be extracted.')
      setTasks(list)
      setStatus('reviewing')
    } catch (err) {
      console.error('Analyze failed:', err)
      setStatus('error')
      setErrorMsg(err.message?.includes('Failed to fetch') || err.message?.includes('Load failed')
        ? 'Could not reach the Mac Studio. Is the tunnel up?'
        : (err.message || 'Failed to analyze the document.'))
    }
  }

  const updateTask = (i, patch) => setTasks((prev) => prev.map((t, j) => (j === i ? { ...t, ...patch } : t)))
  const removeTask = (i) => setTasks((prev) => prev.filter((_, j) => j !== i))
  const setSectionAssignee = (section, assignee) =>
    setTasks((prev) => prev.map((t) => (t.section === section ? { ...t, assignee } : t)))
  const setAllTime = (hours) =>
    setTasks((prev) => prev.map((t) => ({ ...t, timeBudget: hours === '' ? '' : String(hours) })))
  const setSectionTime = (section, hours) =>
    setTasks((prev) => prev.map((t) => (t.section === section ? { ...t, timeBudget: String(hours) } : t)))

  // Total planned time across all tasks, for the summary.
  const totalHours = tasks.reduce((sum, t) => sum + (parseFloat(t.timeBudget) || 0), 0)

  const handleCreate = async () => {
    if (!projectName.trim() || !groupTitle.trim() || tasks.length === 0) return
    setStatus('saving')
    setErrorMsg('')
    try {
      await addTaskGroupWithTasks({
        variables: {
          input: {
            project: projectName.trim(),
            title: groupTitle.trim(),
            description: file ? `Imported from ${file.name}` : null,
            username,
            tasks: tasks.map((t) => ({
              title: t.title,
              type: t.type,
              description: t.section && t.section !== 'General'
                ? `[${t.section}] ${t.description}`
                : t.description,
              priority: t.priority,
              assignee: t.assignee || null,
              timeBudget: t.timeBudget ? parseFloat(t.timeBudget) : null,
              position: 'Backlog',
            })),
          },
        },
      })
      setStatus('completed')
      setTimeout(handleClose, 1500)
    } catch (err) {
      console.error('Create failed:', err)
      setStatus('error')
      setErrorMsg(err.message || 'Failed to create the task group.')
    }
  }

  if (!isOpen) return null

  // Group tasks by section for the review UI.
  const sections = []
  const bySection = {}
  for (const t of tasks) {
    if (!(t.section in bySection)) { bySection[t.section] = []; sections.push(t.section) }
    bySection[t.section].push(t)
  }

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '760px' }}>
        <button className={styles.closeBtn} onClick={handleClose}><CloseIcon /></button>

        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>AI Analysis · Tasks</div>
            <h2 className={styles.title}>Import Tasks from Document</h2>
          </div>
        </div>

        <div className={styles.container}>
          {status !== 'reviewing' && status !== 'completed' && (
            <>
              <div style={{ display: 'flex', gap: '1.5rem' }}>
                <div className={styles.field} style={{ flex: 1 }}>
                  <div className={styles.fieldLabel}>Project</div>
                  <input
                    type="text"
                    className={styles.input}
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="e.g. Cork Matters — pick existing or type new"
                    list="taskgroup-projects"
                  />
                  <datalist id="taskgroup-projects">
                    {existingProjects.map((p) => <option key={p.identity} value={p.properties.title} />)}
                  </datalist>
                </div>
                <div className={styles.field} style={{ flex: 1 }}>
                  <div className={styles.fieldLabel}>Group title</div>
                  <input
                    type="text"
                    className={styles.input}
                    value={groupTitle}
                    onChange={(e) => setGroupTitle(e.target.value)}
                    placeholder="e.g. Content plan"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1.5rem' }}>
                <div className={styles.field} style={{ flex: 1 }}>
                  <div className={styles.fieldLabel}>Document (.docx or .txt)</div>
                  <input type="file" accept=".docx,.txt,.md" onChange={handleFile} className={styles.input} style={{ cursor: 'pointer' }} />
                  {file && <div style={{ marginTop: '0.6rem', fontSize: '0.85rem', color: 'var(--frost-right)' }}>{file.name}</div>}
                </div>
                <div className={styles.field} style={{ flex: 1 }}>
                  <div className={styles.fieldLabel}>Model</div>
                  <select className={styles.input} value={model} onChange={(e) => setModel(e.target.value)}>
                    {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              {status === 'analyzing' && (
                <div style={{ color: 'var(--frost-right)', fontSize: '0.9rem', fontStyle: 'italic' }}>{progressMsg || 'Working…'}</div>
              )}
              {status === 'error' && <div style={{ color: 'var(--frost-wrong)', fontSize: '0.9rem' }}>{errorMsg}</div>}
            </>
          )}

          {status === 'completed' && (
            <div style={{ color: 'var(--frost-right)', fontSize: '0.95rem' }}>Created {tasks.length} tasks under “{projectName}”. 🎉</div>
          )}

          {status === 'reviewing' && (
            <div style={{ maxHeight: '52vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--frost-muted)' }}>
                {tasks.length} tasks → project <strong style={{ color: 'var(--frost-text)' }}>{projectName || '(set a project above)'}</strong>, group <strong style={{ color: 'var(--frost-text)' }}>{groupTitle}</strong>. Tweak, assign, then create.
              </div>

              {/* Bulk time-per-task control */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', padding: '0.55rem 0.7rem', background: '#141416', border: '1px solid var(--frost-border)', borderRadius: '10px' }}>
                <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--frost-right)', fontFamily: "'DM Mono', monospace" }}>Set every task to</span>
                {HOUR_PRESETS.map((h) => (
                  <button
                    key={h}
                    onClick={() => setAllTime(h)}
                    style={{ background: 'var(--frost-field)', color: 'var(--frost-text)', border: '1px solid var(--frost-border)', borderRadius: '999px', fontSize: '0.75rem', padding: '0.25rem 0.7rem', cursor: 'pointer' }}
                  >{h % 1 === 0 ? h : h}h</button>
                ))}
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={bulkHours}
                  onChange={(e) => setBulkHours(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && bulkHours) setAllTime(bulkHours) }}
                  placeholder="custom"
                  style={{ width: '72px', background: 'var(--frost-field)', color: 'var(--frost-text)', border: '1px solid var(--frost-border)', borderRadius: '6px', fontSize: '0.75rem', padding: '0.25rem 0.4rem' }}
                />
                <button
                  onClick={() => bulkHours && setAllTime(bulkHours)}
                  disabled={!bulkHours}
                  style={{ background: 'var(--frost-right-tint)', color: 'var(--frost-right)', border: '1px solid var(--frost-right-border)', borderRadius: '6px', fontSize: '0.75rem', padding: '0.25rem 0.7rem', cursor: bulkHours ? 'pointer' : 'not-allowed', opacity: bulkHours ? 1 : 0.4 }}
                >Apply</button>
                <button
                  onClick={() => setAllTime('')}
                  style={{ background: 'none', color: 'var(--frost-muted)', border: 'none', fontSize: '0.72rem', cursor: 'pointer' }}
                >clear</button>
                <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--frost-muted)', fontFamily: "'DM Mono', monospace" }}>
                  total {totalHours % 1 === 0 ? totalHours : totalHours.toFixed(1)}h
                </span>
              </div>
              {!projectName.trim() && (
                <input
                  type="text"
                  className={styles.input}
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Project name (required)…"
                  list="taskgroup-projects"
                />
              )}
              {sections.map((section) => (
                <div key={section} style={{ border: '1px solid var(--frost-border)', borderRadius: '10px', padding: '0.8rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                    <div style={{ fontWeight: 700, color: 'var(--frost-right)', fontSize: '0.85rem' }}>{section}</div>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <select
                        value=""
                        onChange={(e) => e.target.value && setSectionTime(section, e.target.value)}
                        style={{ background: 'var(--frost-field)', color: 'var(--frost-muted)', border: '1px solid var(--frost-border)', borderRadius: '6px', fontSize: '0.72rem', padding: '0.2rem 0.4rem' }}
                        title="Set hours for every task in this campaign"
                      >
                        <option value="">Time all…</option>
                        {HOUR_PRESETS.map((h) => <option key={h} value={h}>{h}h each</option>)}
                      </select>
                      <select
                        value=""
                        onChange={(e) => e.target.value && setSectionAssignee(section, e.target.value === 'none' ? '' : e.target.value)}
                        style={{ background: 'var(--frost-field)', color: 'var(--frost-muted)', border: '1px solid var(--frost-border)', borderRadius: '6px', fontSize: '0.72rem', padding: '0.2rem 0.4rem' }}
                      >
                        <option value="">Assign all to…</option>
                        {ASSIGNEES.map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
                        <option value="none">Unassign all</option>
                      </select>
                    </div>
                  </div>
                  {bySection[section].map((t) => {
                    const globalIdx = tasks.indexOf(t)
                    return (
                      <div key={globalIdx} style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', padding: '0.5rem 0', borderTop: '1px solid #202024' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <input
                            className={styles.input}
                            style={{ flex: 1, margin: 0 }}
                            value={t.title}
                            onChange={(e) => updateTask(globalIdx, { title: e.target.value })}
                          />
                          <button
                            onClick={() => removeTask(globalIdx)}
                            title="Remove"
                            style={{ background: 'none', border: 'none', color: 'var(--frost-wrong)', cursor: 'pointer', fontSize: '1.1rem' }}
                          >✕</button>
                        </div>
                        {t.description && <div style={{ fontSize: '0.78rem', color: 'var(--frost-muted)' }}>{t.description}</div>}
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <select value={t.type} onChange={(e) => updateTask(globalIdx, { type: e.target.value })}
                            style={{ background: 'var(--frost-field)', color: 'var(--frost-text)', border: '1px solid var(--frost-border)', borderRadius: '6px', fontSize: '0.75rem', padding: '0.25rem 0.4rem' }}>
                            {TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
                          </select>
                          <select value={t.priority} onChange={(e) => updateTask(globalIdx, { priority: e.target.value })}
                            style={{ background: 'var(--frost-field)', color: 'var(--frost-text)', border: '1px solid var(--frost-border)', borderRadius: '6px', fontSize: '0.75rem', padding: '0.25rem 0.4rem' }}>
                            {PRIORITIES.map((x) => <option key={x} value={x}>{x}</option>)}
                          </select>
                          <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }} title="Assignee">
                            {ASSIGNEES.map((a) => (
                              <img
                                key={a.name}
                                src={a.image}
                                alt={a.name}
                                title={a.name}
                                onClick={() => updateTask(globalIdx, { assignee: t.assignee === a.name ? '' : a.name })}
                                style={{
                                  width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer',
                                  border: t.assignee === a.name ? '2px solid var(--frost-right)' : '2px solid transparent',
                                  opacity: t.assignee && t.assignee !== a.name ? 0.35 : 1,
                                  transition: 'all 0.15s',
                                }}
                              />
                            ))}
                          </div>
                          <input
                            type="number"
                            value={t.timeBudget}
                            onChange={(e) => updateTask(globalIdx, { timeBudget: e.target.value })}
                            placeholder="hrs"
                            style={{ width: '60px', background: 'var(--frost-field)', color: 'var(--frost-text)', border: '1px solid var(--frost-border)', borderRadius: '6px', fontSize: '0.75rem', padding: '0.25rem 0.4rem' }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
              {status === 'error' && <div style={{ color: 'var(--frost-wrong)', fontSize: '0.9rem' }}>{errorMsg}</div>}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {(status === 'idle' || status === 'analyzing' || status === 'error') && (
            <button className={styles.primaryBtn} onClick={handleAnalyze} disabled={!file || status === 'analyzing'}>
              {status === 'analyzing' ? 'Analyzing…' : 'Analyze Document'}
            </button>
          )}
          {status === 'reviewing' && (
            <button className={styles.primaryBtn} onClick={handleCreate} disabled={!projectName.trim() || tasks.length === 0}>
              Create {tasks.length} tasks
            </button>
          )}
          {status === 'saving' && <button className={styles.primaryBtn} disabled>Creating…</button>}
        </div>
      </div>
    </div>,
    document.body
  )
}
